/**
 * Configure Step Component
 *
 * Second step in the create table sheet wizard.
 * Uses shared FieldsTable component with expandable rows for editing.
 *
 * @module features/workspace/components/CreateTableSheet/steps/ConfigureStep
 */

import { useMemo } from 'react';
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { Alert, AlertDescription, Button, Input, Switch, Label } from '@loader/ui';
import type { FieldDataType, CreateTableFieldConfig } from '@loader/shared-types';

import type { ConfigureStepProps } from '../types';
import { useCanGoBack } from '../../../stores/useCreateTableStore';

import { FieldsTable, InlineTypeSelector } from '@/features/schemas/components';
import { validateCreateTableFields } from '@/features/schemas/utils/fieldValidation';

// ============================================================================
// EXPANDED ROW CONTENT
// ============================================================================

interface ExpandedContentProps {
  field: CreateTableFieldConfig;
  onUpdate: (updates: Partial<CreateTableFieldConfig>) => void;
  disabled?: boolean;
}

function ExpandedContent({ field, onUpdate, disabled }: ExpandedContentProps) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 h-8">
        <Label className="text-sm text-muted-foreground w-20 shrink-0">Type</Label>
        <InlineTypeSelector
          dataType={field.dataType}
          onTypeChange={(type: FieldDataType) => onUpdate({ dataType: type })}
          disabled={disabled}
        />
      </div>

      <div className="flex items-center gap-3 h-8">
        <Label className="text-sm text-muted-foreground w-20 shrink-0">Description</Label>
        <Input
          placeholder="Optional description..."
          value={field.description || ''}
          onChange={(e) => onUpdate({ description: e.target.value })}
          disabled={disabled}
          className="h-8"
        />
      </div>

      <div className="flex items-center gap-3 h-8">
        <Label className="text-sm text-muted-foreground w-20 shrink-0">Required</Label>
        <Switch
          checked={field.isRequired}
          onCheckedChange={(checked) => onUpdate({ isRequired: checked })}
          disabled={disabled}
        />
      </div>

      <div className="flex items-center gap-3 h-8">
        <Label className="text-sm text-muted-foreground w-20 shrink-0">Unique</Label>
        <Switch
          checked={field.isUnique}
          onCheckedChange={(checked) => onUpdate({ isUnique: checked })}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ConfigureStep({
  fields,
  onUpdateField,
  isSaving,
  onContinue,
  onBack,
}: ConfigureStepProps) {
  const canGoBack = useCanGoBack();

  // Validate fields
  const validation = useMemo(() => validateCreateTableFields(fields), [fields]);
  const isValid = fields.length > 0 && validation.isValid;

  return (
    <div className="flex flex-1 flex-col gap-4 p-4 min-h-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">Configure Fields</h3>
        <p className="text-xs text-muted-foreground">
          {fields.length} field{fields.length !== 1 ? 's' : ''} detected
        </p>
      </div>

      {/* Validation error */}
      {!validation.isValid && validation.error && (
        <Alert variant="destructive" className="py-2">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{validation.error}</AlertDescription>
        </Alert>
      )}

      {/* Fields table - scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto -mx-4">
        <div className="px-4">
          <FieldsTable
            fields={fields}
            disabled={isSaving}
            renderExpanded={(field) => (
              <ExpandedContent
                field={field}
                onUpdate={(updates) => onUpdateField(field.id, updates)}
                disabled={isSaving}
              />
            )}
            emptyMessage="No fields detected"
          />
        </div>
      </div>

      {/* Navigation buttons */}
      <div className="flex gap-2 mt-auto">
        {canGoBack && (
          <Button
            variant="outline"
            onClick={onBack}
            disabled={isSaving}
            className="flex-1"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
        <Button
          onClick={onContinue}
          disabled={!isValid || isSaving}
          className="flex-1"
        >
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              Continue
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
